import { Collapse, Table } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector, useAppDispatch } from "src/hooks/redux";
import { convertToLocalAndFormatDate, formatTime } from "../../utils/Utils";
import SubmitAgainIcon from "../Shared/icons/SubmitAgainIcon";
import DollarIcon from "../Shared/icons/DollarIcon";
import SubmitCreditScreeningModal from "./SubmitCreditScreeningModal";
import ConfirmIncreasedDepositModal from "./ConfirmIncreasedDepositModal";
import StatusTag from "../Shared/StatusTag";
import {
  Applicant,
  ApplicantType,
  ScreeningVendor,
} from "../../types/Applicant";
import {
  ApplicationStage,
  ApplicationStatusId,
  ReduxApplication,
} from "../../types/Application";
import { ScreeningInfo, getScreeningStatuses } from "src/utils/Screening";
import { ScreeningStatusMap, ScreeningTypes } from "src/types/Screening";
import Chip from "@mui/material/Chip";
import {
  getIsIncSecDepositFeatureEnabled,
  getConfigurations,
  getIsCommunityIssueResolutionFeatureEnabled,
} from "src/services/AdminDashService";
import type { Configuration } from "src/types/Redux";
import Checkbox from "@mui/material/Checkbox";
import ResolveAlertModal from "./ResolveAlertModal";

interface ScreeningProps {
  application: ReduxApplication;
}

interface ApplicantExtendedScreening extends Applicant {
  onfidoStatus?: string;
  fadvStatus?: string;
  lastUpdated?: string;
  issues?: { [key: string]: any };
}

const Screening = ({ application }: ScreeningProps) => {
  const [applicantData, setApplicantData] = useState([]);
  const [guarantorData, setGuarantorData] = useState([]);
  const notification = useAppSelector(
    (state) => state.applications.notification
  );
  const [formattedApplication, setFormattedApplication] = useState<
    ReduxApplication | undefined
  >();
  const [applicantColumns, setApplicantColumns] = useState([]);
  const [guarantorColumns, setGuarantorColumns] = useState([]);
  const { Panel } = Collapse;
  const [latestDate, setLatestDate] = useState("");
  const [showCreditScreeningModal, setShowCreditScreeningModal] =
    useState(false);
  const [
    showConfirmIncreasedDepositModal,
    setShowConfirmIncreasedDepositModal,
  ] = useState(false);
  const [hasScreeningAlerts, setHasScreeningAlerts] = useState(false);
  const [selectedIssueIds, setSelectedIssueIds] = useState<number[]>([]);
  const [showResolveAlertModal, setShowResolveAlertModal] = useState(false);
  const [selectedApplicantForResolve, setSelectedApplicantForResolve] =
    useState<{ fullName: string; applicantId: number; issueId: number } | null>(
      null
    );
  const dispatch = useAppDispatch();
  const configuration = useAppSelector<Configuration>(
    (state) => state.applications.configuration ?? ({} as Configuration)
  );

  const isIssueResolutionEnabled = useMemo(
    () =>
      getIsCommunityIssueResolutionFeatureEnabled(
        configuration?.features,
        application?.communityId
      ),
    [configuration, application]
  );

  useEffect(() => {
    if (!configuration) {
      dispatch(getConfigurations());
    }
  }, [configuration, dispatch]);

  useEffect(() => {
    if (applicantData?.length > 0 || guarantorData?.length > 0) {
      prepColumns("Adults", applicantData.length);
      prepColumns("Guarantors", guarantorData.length);
    }
  }, [
    selectedIssueIds,
    isIssueResolutionEnabled,
    formattedApplication?.applicationStatusId,
    applicantData?.length,
    guarantorData?.length,
  ]);

  useEffect(() => {
    application && prepareApplicationData(application);
  }, [application]);

  const prepareApplicationData = (app: ReduxApplication): void => {
    const formattedApplicants = [...app.applicants].map(
      ({ ...applicant }: ApplicantExtendedScreening) => {
        const { applicantScreening } = applicant;
        const screeningStatuses = getScreeningStatuses(
          applicantScreening,
          setLatestDate
        );
        let onfidoStatus: string | null = null;
        let fadvStatus: string | null = null;

        if (!screeningStatuses) return applicant;
        const applicantIssues: any = {};
        Object.values<ScreeningInfo>(screeningStatuses).forEach((screening) => {
          if (screening.issues) {
            screening.issues.map((issue) => {
              if (!applicantIssues[issue.type]) {
                applicantIssues[issue.type] = [issue];
              } else {
                applicantIssues[issue.type].push(issue);
              }
            });
          }
        });

        const ScreeningStatuses = {
          Other: "Not Started",
          Pending: "Pending",
          Approved: "Approved",
          ApprovedWithConditions: "Approved With Conditions",
          ApprovedWithConditionsMet: "AWC, conditions met",
          Retry: "Retry",
          FurtherReview: "Action Required",
          ProblemResolution: "Problem Resolution",
          Declined: "Declined",
          Cancelled: "Cancelled",
        };
        if (!!screeningStatuses["onfido"]) {
          onfidoStatus =
            ScreeningStatuses[screeningStatuses["onfido"].screeningStatus] ??
            screeningStatuses["onfido"].screeningStatus;
        }

        if (!!screeningStatuses["fadv"]) {
          fadvStatus =
            ScreeningStatuses[screeningStatuses["fadv"].screeningStatus] ??
            screeningStatuses["fadv"].screeningStatus;
        }
        if (Object.keys(applicantIssues).length > 0) {
          setHasScreeningAlerts(true);
        }
        applicant.onfidoStatus = onfidoStatus || "Not Started"; // action - credit screening outcome
        applicant.fadvStatus = fadvStatus || ""; // status - id screening outcome
        applicant.lastUpdated = screeningStatuses["latestDate"]
          ? screeningStatuses["latestDate"]
          : "";
        applicant.issues = Object.keys(applicantIssues).length
          ? applicantIssues
          : null;
        return applicant;
      }
    );

    setFormattedApplication({
      ...app,
      applicants: formattedApplicants as Applicant[],
    });
    prepareApplicantsAndGuarantorsData(
      formattedApplicants as ApplicantExtendedScreening[]
    );
  };

  const prepColumns = (title: string, itemCount: number) => {
    const baseColumns: any = [
      {
        title: `${title} (${itemCount})`,
        dataIndex: "fullName",
        key: "fullName",
        width: "25%",
      },
      {
        title: "Credit Screening Status",
        dataIndex: "credit",
        key: "credit",
      },
      {
        title: "Screening Alerts",
        dataIndex: "alerts",
        key: "alerts",
      },
    ];

    if (isIssueResolutionEnabled) {
      baseColumns.push({
        title: "Resolve Alert",
        key: "checkbox",
        render: (_: any, record: any) => {
          const issues = Object.values(record?.issues || {}).flat();

          if (!issues.length) return "-";

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                justifyContent: "center",
              }}
            >
              {issues.map((issue: any) => {
                const isChecked = selectedIssueIds.includes(issue.id);
                const isResolved = issue.resolved;

                return (
                  <div
                    key={issue.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Checkbox
                      checked={isChecked || isResolved}
                      disabled={
                        isChecked ||
                        isResolved ||
                        record.isSoftDelete ||
                        (formattedApplication?.applicationStatusId !==
                          ApplicationStatusId.InProgress &&
                          formattedApplication?.applicationStatusId !==
                            ApplicationStatusId.LeaseReady)
                      }
                      onChange={() => {
                        if (!isChecked) {
                          setSelectedApplicantForResolve({
                            fullName: record.fullName,
                            applicantId: record.applicantId,
                            issueId: issue.id,
                          });
                          setShowResolveAlertModal(true);
                        } else {
                          const newIds = selectedIssueIds.filter(
                            (id) => id !== issue.id
                          );
                          setSelectedIssueIds(newIds);
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
          );
        },
      });
    }

    baseColumns.push(
      {
        title: "ID Fraud Status",
        dataIndex: "identity",
        key: "identity",
      },
      {
        title: "Last Update",
        dataIndex: "modifiedDate",
        key: "modifiedDate",
      }
    );

    if (title === "Adults") setApplicantColumns(baseColumns);
    else if (title === "Guarantors") setGuarantorColumns(baseColumns);
  };

  const prepareApplicantsAndGuarantorsData = (
    applicants: ApplicantExtendedScreening[]
  ) => {
    const arr: any = [];
    const guarantors: any = [];
    const len = applicants.length;
    for (let i = 0; i < len; i++) {
      const tData = applicants[i];
      // some applicationProcess is null or undefined
      const formattedDate = tData.lastUpdated
        ? `${formatTime(tData.lastUpdated)}, ${convertToLocalAndFormatDate(
            tData.lastUpdated
          )}`
        : "";
      const fullName = `${tData?.firstName || ""} ${
        tData?.lastName || ""
      }`.trim();
      const data = {
        fullName: (
          <>
            {fullName}
            {tData?.isSoftDelete && (
              <>
                {" "}
                <Chip color="warning" label="Removed" size="small" />
              </>
            )}
          </>
        ),
        credit: tData.fadvStatus ?? "",
        identity: tData.onfidoStatus ?? "",
        modifiedDate: formattedDate,
        alerts: tData?.issues
          ? Object.entries<[string, any]>(tData.issues).map(
              ([issueType, issues]) =>
                issues.map((issue, index) => (
                  <>
                    <span
                      key={issue.id}
                      className={`alert-type-${issueType.toLocaleLowerCase()}`}
                    >
                      {issues.length > 1
                        ? `${ScreeningTypes[issueType] ?? issueType} (${
                            index + 1
                          }): `
                        : `${ScreeningTypes[issueType] ?? issueType}:`}
                    </span>
                    <span>{` ${issue.description}`}</span>
                    <br />
                  </>
                ))
            )
          : "-",
        applicantId: tData?.id,
        resolved: Object.values(tData?.issues || {})
          .flat()
          .every((i) => i.resolved),
        issues: tData.issues ?? null,
        isSoftDelete: tData.isSoftDelete || false,
      };
      if (
        applicants[i].applicantTypeId === ApplicantType.Applicant ||
        applicants[i].applicantTypeId === ApplicantType.CoTenant
      ) {
        arr.push(data);
      } else {
        guarantors.push(data);
      }
    }
    setApplicantData(arr);
    setGuarantorData(guarantors);
  };

  const showSubmitAgain = () => {
    const { applicants } = application || {};

    if (!applicants) {
      return false;
    }

    const everyoneInTwoStepScreening = applicants
      .filter((a) => !a.isSoftDelete)
      .every(
        ({ applicationProcess, isSoftDelete }) =>
          applicationProcess &&
          applicationProcess.applicationStageId ===
            ApplicationStage.TwoStepScreening &&
          !isSoftDelete
      );

    const hasNoFADV = applicants.every(
      ({ applicantScreening, isSoftDelete }) => {
        if (
          applicantScreening !== null &&
          applicantScreening.length > 0 &&
          !isSoftDelete
        ) {
          return applicantScreening.every((s) => {
            return (
              s.screeningVendor !== ("Fadv" as ScreeningVendor) &&
              s.screeningVendor !== "FADV"
            );
          });
        }
        return true;
      }
    );

    const hasGuarantorWithCompleteStatus = applicants.some(
      ({ applicantTypeId, applicationProcess, isSoftDelete }) =>
        applicantTypeId === ApplicantType.Guarantor &&
        applicationProcess?.applicationStatusId ===
          ApplicationStatusId.ApplicationComplete &&
        !isSoftDelete
    );

    const allApplicantsAndCoTenantsHaveCreditAndIDReturned = applicants
      .filter(
        ({ applicantTypeId, isSoftDelete }) =>
          (applicantTypeId === ApplicantType.Applicant ||
            applicantTypeId === ApplicantType.CoTenant) &&
          !isSoftDelete
      )
      .every(
        ({ applicationProcess }) =>
          applicationProcess &&
          applicationProcess.applicationStatusId ===
            ApplicationStatusId.CreditAndIDReturned
      );

    return (
      everyoneInTwoStepScreening &&
      (hasNoFADV ||
        (hasGuarantorWithCompleteStatus &&
          allApplicantsAndCoTenantsHaveCreditAndIDReturned))
    );
  };

  const getPrimaryApplicant = () => {
    if (!formattedApplication) return null;
    return formattedApplication.applicants.find(
      (x) => x.applicantTypeId === ApplicantType.Applicant
    );
  };

  const hasFadvRequestSuccess =
    !!notification &&
    notification.type === "INFO" &&
    notification?.message &&
    notification.message.includes("Credit Screening for");

  const hasCreditScreeningSuccess =
    !!notification &&
    notification.type === "INFO" &&
    notification?.message &&
    notification.message.includes(
      "Condition Met, increased security deposit met for"
    );

  // show incr sec dep button if:
  // 1. applicationScreeningResultId is 2 (Accepted)
  // 2. hasCreditScreeningSuccess is false
  // 3. payment feature is not configured (not deployed)
  // 4. new incr sec deposit feature is not enabled for this community (old flow)
  const showConfirmIncreasedDepositButton =
    formattedApplication &&
    formattedApplication.applicationScreeningResultId === 2 &&
    !hasCreditScreeningSuccess &&
    (!configuration?.features?.payment || // show if payment feature is not configured (not deployed)
      !getIsIncSecDepositFeatureEnabled(
        // do not show if new incr sec deposit feature is enabled
        configuration.features,
        formattedApplication.communityId
      ));

  /**
   * NOTE: Expected values for overAllScreeningStatuses
   *  are "Pending", "Declined" and "Accepted"
   */
  return (
    <div id="avb-screening">
      <Collapse defaultActiveKey={"1"} bordered={false}>
        <Panel header={`SCREENING & ID VERIFICATION OUTCOMES`} key="1">
          <div className="row">
            <div className="blue-card col-sm-12 col-md-4">
              <h3>Group Screening Outcome</h3>
              <p>GROUP DECISION</p>
              {formattedApplication &&
                !!formattedApplication.adminDashApplicationScreeningResultId && (
                  <div className="col tags">
                    <p className="tagPlaceHolder">
                      <StatusTag
                        name={
                          ScreeningStatusMap[
                            formattedApplication
                              .adminDashApplicationScreeningResultId
                          ]
                        }
                      />
                    </p>
                    {hasScreeningAlerts && (
                      <p className="tagPlaceHolder">
                        <StatusTag name="Screening alert" />
                      </p>
                    )}
                  </div>
                )}
              <p>
                TIME RECEIVED
                <span>{latestDate}</span>
              </p>
              {!hasFadvRequestSuccess && showSubmitAgain() && (
                <button
                  className="submit-again"
                  onClick={() => setShowCreditScreeningModal(true)}
                >
                  <SubmitAgainIcon /> Submit again
                </button>
              )}
              {showConfirmIncreasedDepositButton && (
                <button
                  className="increase-deposit"
                  onClick={() => setShowConfirmIncreasedDepositModal(true)}
                >
                  <DollarIcon /> Confirm Increased Deposit Paid
                </button>
              )}
            </div>
            <div className=" col-sm-12 col-md-8">
              <Table
                dataSource={applicantData}
                columns={applicantColumns}
                pagination={false}
              />
              <Table
                dataSource={guarantorData}
                columns={guarantorColumns}
                pagination={false}
              />
            </div>
          </div>
        </Panel>
      </Collapse>
      <SubmitCreditScreeningModal
        applicationId={
          formattedApplication && formattedApplication.applicationId
        }
        applicant={getPrimaryApplicant()}
        showModal={showCreditScreeningModal}
        setShowModal={setShowCreditScreeningModal}
      />
      {formattedApplication && (
        <ConfirmIncreasedDepositModal
          application={formattedApplication}
          showModal={showConfirmIncreasedDepositModal}
          setShowModal={setShowConfirmIncreasedDepositModal}
        />
      )}
      {showResolveAlertModal && selectedApplicantForResolve && (
        <ResolveAlertModal
          showModal={showResolveAlertModal}
          setShowModal={setShowResolveAlertModal}
          applicant={{
            fullName: selectedApplicantForResolve?.fullName ?? "",
            applicantId: selectedApplicantForResolve?.applicantId,
            issueId: selectedApplicantForResolve?.issueId ?? 0,
          }}
          onConfirmResolve={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setSelectedIssueIds((prev) => [
                ...prev,
                selectedApplicantForResolve?.issueId,
              ]);
            }
            setShowResolveAlertModal(false);
          }}
          onCancel={() => {
            setShowResolveAlertModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Screening;
